/**
 * ผลรางวัล — ตรง user-classic resultDisplayUtils
 */
import type { LotteryRound } from '../services/lotteryService'
import { compareRoundsForLottoDisplay } from '../services/lotteryService'

export function getWinningNumbers(round: LotteryRound | null) {
  const wn = (round as { results?: { winningNumbers?: Record<string, string> } } | null)?.results?.winningNumbers
  if (!wn) return { first: '', threeUp: '', twoDown: '', threeDown: '', fourUp: '', twoUp: '' }
  const first = wn.first ?? (wn as Record<string, string>).FIRST_PRIZE ?? ''
  const twoDown =
    wn.twoDown ??
    (wn as Record<string, string>).LAST_2_DIGITS ??
    (wn as Record<string, string>)['2_DIGIT_BOTTOM'] ??
    ''
  const threeUp = wn.threeUp ?? (wn as Record<string, string>)['3_DIGIT_TOP'] ?? ''
  const threeDown =
    wn.threeDown ??
    (wn as Record<string, string>)['3_DIGIT_BOTTOM_FRONT_1'] ??
    (wn as Record<string, string>)['3_DIGIT_BOTTOM_BACK_1'] ??
    ''
  const resolvedThreeUp = threeUp || (first.length >= 3 ? first.slice(-3) : '')
  const resolvedTwoUp =
    (wn as Record<string, string>).twoUp ??
    (wn as Record<string, string>)['2_DIGIT_TOP'] ??
    (resolvedThreeUp.length >= 2 ? resolvedThreeUp.slice(-2) : '')

  return {
    first,
    threeUp: resolvedThreeUp,
    twoDown,
    threeDown,
    fourUp:
      (wn as Record<string, string>).fourUp ??
      (wn as Record<string, string>)['4_DIGIT_TOP'] ??
      (wn as Record<string, string>)['4_DIGIT_EXACT'] ??
      '',
    twoUp: resolvedTwoUp,
  }
}

function collectBottomThreeCandidates(
  raw: Record<string, string>,
  resolved: ReturnType<typeof getWinningNumbers>,
): string[] {
  const candidates: string[] = []
  const push = (value: string) => {
    const trimmed = value.trim()
    if (!trimmed) return
    // แยก "683,709,746,427" เป็น chip ทีละเลข — ห้าม push string รวมทั้งก้อน
    trimmed.split(/[,，\s]+/).forEach((part) => {
      const token = part.trim()
      if (!token || candidates.includes(token)) return
      candidates.push(token)
    })
  }

  ;[
    '3_DIGIT_BOTTOM_FRONT_1',
    '3_DIGIT_BOTTOM_FRONT_2',
    '3_DIGIT_BOTTOM_BACK_1',
    '3_DIGIT_BOTTOM_BACK_2',
    '3_DIGIT_FRONT',
    '3_DIGIT_BACK',
    '3_DIGIT_BOTTOM',
  ].forEach((key) => push(raw[key] ?? ''))

  push(resolved.threeDown)
  return candidates
}

/** เลข 3 ตัวล่าง หวยรัฐบาล — รวมทุกช่อง (หน้า/หลัง) ไม่ซ้ำ */
export function getGovernmentThreeDownList(round: LotteryRound | null): string[] {
  if (!round) return []
  const raw =
    (round as { results?: { winningNumbers?: Record<string, string> } }).results?.winningNumbers ?? {}
  return collectBottomThreeCandidates(raw, getWinningNumbers(round))
}

/** เลขที่ออกตามประเภทรางวัล — สอดคล้องกับ BetSlip.getMatchedResultNumber บน backend */
export function getMatchedResultNumberForPrizeType(
  prizeTypeCode: string,
  round: LotteryRound | null,
): string {
  if (!round) return ''

  const raw =
    (round as { results?: { winningNumbers?: Record<string, string> } }).results?.winningNumbers ?? {}
  const yeekee = (
    round as {
      yeekee?: { result?: { threeDigitTop?: string; twoDigitTop?: string; twoDigitDown?: string } }
    }
  ).yeekee?.result
  const base = getWinningNumbers(round)

  const threeUp = base.threeUp || yeekee?.threeDigitTop || ''
  const twoUp = base.twoUp || yeekee?.twoDigitTop || raw['2_DIGIT_TOP'] || ''
  const twoDown = base.twoDown || yeekee?.twoDigitDown || ''
  const fourUp = base.fourUp || raw['4_DIGIT_TOP'] || raw['4_DIGIT_EXACT'] || ''
  const first = base.first || raw.FIRST_PRIZE || ''

  let code = String(prizeTypeCode || '').trim().toUpperCase()
  if (code === '3_DIGIT_REVERSE') code = '3_DIGIT_TOP'
  if (code === '2_DIGIT_REVERSE') code = '2_DIGIT_TOP'

  if (code === '3_DIGIT_BOTTOM') {
    const candidates = collectBottomThreeCandidates(raw, base)
    if (candidates.length > 0) return candidates.join(', ')
    return base.threeDown || raw['3_DIGIT_BOTTOM'] || ''
  }

  const mapping: Record<string, string> = {
    '3_DIGIT_TOP': threeUp,
    '3_DIGIT_TOD': threeUp,
    '2_DIGIT_TOP': twoUp,
    '2_DIGIT_BOTTOM': twoDown,
    LAST_2_DIGITS: twoDown,
    '2_DIGIT_TOD': twoDown || twoUp,
    '2_DIGIT_FRONT': raw['2_DIGIT_FRONT'] ?? '',
    '2_DIGIT_BACK': raw['2_DIGIT_BACK'] || twoDown,
    '4_DIGIT_EXACT': fourUp || first,
    '4_DIGIT_TOP': fourUp || first,
    '4_DIGIT_TOD': fourUp || first,
    FIRST_PRIZE: fourUp || first,
    RUNNING_TOP: threeUp,
    RUNNING_BOTTOM: twoDown,
    FIXED_POSITION_ONES: raw.FIXED_POSITION_ONES || (threeUp ? threeUp.slice(-1) : ''),
    FIXED_POSITION_TENS: raw.FIXED_POSITION_TENS || (threeUp.length >= 2 ? threeUp.slice(-2, -1) : ''),
    FIXED_POSITION_HUNDREDS:
      raw.FIXED_POSITION_HUNDREDS || (threeUp.length >= 3 ? threeUp.slice(-3, -2) : ''),
  }

  const result = mapping[code] ?? raw[code] ?? ''
  return String(result || '').trim()
}

export function hasAnyResult(round: LotteryRound | null): boolean {
  const w = getWinningNumbers(round)
  return !!(w.first || w.threeUp || w.twoDown || w.threeDown || w.fourUp || w.twoUp)
}

export function isRoundCancelled(round: LotteryRound | null | undefined): boolean {
  return String(round?.status || '').toLowerCase() === 'cancelled'
}

export function displayValue(
  value: string,
  options: { isCancelled?: boolean; hasResult?: boolean } = {}
): string {
  const { isCancelled = false, hasResult = true } = options
  if (isCancelled) return 'ยกเลิก'
  if (!hasResult || !value) return 'รอผล'
  return value
}

/** เรียงรอบบนหน้าผล — งวดตรงวันที่เลือกก่อน แล้วเรียงเปิด → ยังไม่เปิด → ปิดรับแทง → ยกเลิก */
export function sortRoundsForResultDisplay<T extends LotteryRound>(list: T[], selectedDate: string): T[] {
  return [...list].sort((a, b) => {
    const aDraw = getRoundDrawDateString(a)
    const bDraw = getRoundDrawDateString(b)
    const aMatch = aDraw === selectedDate ? 1 : 0
    const bMatch = bDraw === selectedDate ? 1 : 0
    if (bMatch !== aMatch) return bMatch - aMatch
    return compareRoundsForLottoDisplay(a, b)
  })
}

/** งวด: DD MMM YYYY (en-US for month short) */
function formatDrawLabelFromDate(date: Date): string {
  const f = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Bangkok',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
  const parts = f.formatToParts(date)
  const day = parts.find((p) => p.type === 'day')?.value ?? String(date.getDate())
  const month = parts.find((p) => p.type === 'month')?.value ?? ''
  const year = parts.find((p) => p.type === 'year')?.value ?? String(date.getFullYear())
  return `งวด: ${day} ${month} ${year}`
}

/** งวดจากรอบ — fallback เป็นวันที่ที่เลือกบนหน้าเมื่อ API ไม่มี betEndTime */
export function getRoundDrawLabel(round: LotteryRound | null, fallbackDateStr?: string): string {
  if (round?.betEndTime) {
    const d = new Date(round.betEndTime)
    if (!Number.isNaN(d.getTime())) return formatDrawLabelFromDate(d)
  }
  if (round?.drawDate) {
    const d = new Date(round.drawDate)
    if (!Number.isNaN(d.getTime())) return formatDrawLabelFromDate(d)
  }
  const fromRoundNumber = round?.roundNumber ? getDateStringFromRoundNumber(round.roundNumber) : null
  if (fromRoundNumber) {
    const d = new Date(`${fromRoundNumber}T12:00:00+07:00`)
    if (!Number.isNaN(d.getTime())) return formatDrawLabelFromDate(d)
  }
  if (fallbackDateStr?.trim()) {
    const d = new Date(`${fallbackDateStr.trim()}T12:00:00+07:00`)
    if (!Number.isNaN(d.getTime())) return formatDrawLabelFromDate(d)
  }
  return 'งวด: -'
}

export function getDrawLabelNgod(round: LotteryRound, fallbackDateStr?: string): string {
  return getRoundDrawLabel(round, fallbackDateStr)
}

const BANGKOK = 'Asia/Bangkok'

function toDateStringBangkok(iso: string): string {
  const f = new Intl.DateTimeFormat('en-CA', { timeZone: BANGKOK, year: 'numeric', month: '2-digit', day: '2-digit' })
  const parts = f.formatToParts(new Date(iso))
  const y = parts.find((p) => p.type === 'year')!.value
  const m = parts.find((p) => p.type === 'month')!.value
  const d = parts.find((p) => p.type === 'day')!.value
  return `${y}-${m}-${d}`
}

export function isRoundVisibleOnDate(round: LotteryRound, dateStr: string): boolean {
  if (!dateStr?.trim()) return true
  const startDate = toDateStringBangkok(round.betStartTime)
  const endDate = toDateStringBangkok(round.betEndTime)
  const drawDate = round.drawDate ? toDateStringBangkok(round.drawDate) : endDate
  if (drawDate === dateStr) return true
  if (dateStr >= startDate && dateStr <= endDate) return true
  return false
}

export function getRoundDrawDateString(round: LotteryRound): string {
  return round.drawDate ? toDateStringBangkok(round.drawDate) : toDateStringBangkok(round.betEndTime)
}

export function getDateStringFromRoundNumber(roundNumber: string | undefined): string | null {
  if (!roundNumber?.trim()) return null
  const m = roundNumber.match(/^[^-]+-(\d{8})-/)
  if (!m) return null
  const y = m[1].slice(0, 4)
  const mo = m[1].slice(4, 6)
  const d = m[1].slice(6, 8)
  return `${y}-${mo}-${d}`
}

export function isRoundNumberDateExactly(round: LotteryRound, dateStr: string): boolean {
  if (!dateStr?.trim()) return true
  const fromRoundNumber = getDateStringFromRoundNumber(round.roundNumber)
  if (fromRoundNumber != null) return fromRoundNumber === dateStr
  return getRoundDrawDateString(round) === dateStr
}
